import { ref, onUnmounted } from 'vue'
import store from '@/store/index'

const API_BASE = store.state.apiUrl

const request = async (method, path, body = null) => {
  const url = `${API_BASE}${path}`
  const options = {
    method,
    headers: { 'Content-Type': 'application/json' },
  }
  if (body) {
    options.body = JSON.stringify(body)
  }
  const res = await fetch(url, options)
  const data = await res.json()
  if (!res.ok) {
    throw new Error(data.detail || `Request failed with status ${res.status}`)
  }
  return data
}

export default function useNotifications() {
  const notifications = ref([])
  const unreadCount = ref(0)
  const loading = ref(false)
  const error = ref(null)
  let pollInterval = null

  const fetchNotifications = async (filters = {}) => {
    loading.value = true
    error.value = null
    try {
      const params = new URLSearchParams()
      if (filters.site_id) params.append('site_id', filters.site_id)
      if (filters.status) params.append('status', filters.status)
      if (filters.severity) params.append('severity', filters.severity)
      if (filters.limit) params.append('limit', filters.limit)
      if (filters.offset) params.append('offset', filters.offset)
      const qs = params.toString()
      notifications.value = await request('GET', `/notifications${qs ? '?' + qs : ''}`)
    } catch (e) {
      error.value = e.message
      console.error('[Notifications] Fetch error:', e)
    } finally {
      loading.value = false
    }
  }

  const fetchUnreadCount = async () => {
    try {
      const data = await request('GET', '/notifications/unread-count')
      unreadCount.value = data.count
    } catch (e) {
      console.error('[Notifications] Unread count error:', e)
    }
  }

  const markRead = async (id) => {
    try {
      await request('PUT', `/notifications/${id}/read`)
      const notif = notifications.value.find((n) => n.id === id)
      if (notif) notif.status = 'read'
      unreadCount.value = Math.max(0, unreadCount.value - 1)
    } catch (e) {
      console.error('[Notifications] Mark read error:', e)
    }
  }

  const markResolved = async (id, userId) => {
    try {
      await request('PUT', `/notifications/${id}/resolve`, { resolvedBy: userId })
      const notif = notifications.value.find((n) => n.id === id)
      if (notif) notif.status = 'resolved'
    } catch (e) {
      console.error('[Notifications] Resolve error:', e)
    }
  }

  const markAllRead = async () => {
    try {
      await request('POST', '/notifications/mark-all-read')
      notifications.value.forEach((n) => {
        if (n.status === 'unread') n.status = 'read'
      })
      unreadCount.value = 0
    } catch (e) {
      console.error('[Notifications] Mark all read error:', e)
    }
  }

  const startPolling = (intervalMs = 60000) => {
    stopPolling()
    fetchUnreadCount()
    pollInterval = setInterval(fetchUnreadCount, intervalMs)
  }

  const stopPolling = () => {
    if (pollInterval) {
      clearInterval(pollInterval)
      pollInterval = null
    }
  }

  onUnmounted(() => {
    stopPolling()
  })

  return {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    fetchUnreadCount,
    markRead,
    markResolved,
    markAllRead,
    startPolling,
    stopPolling,
  }
}
